import { Box } from '@mui/material'
import { forwardRef } from 'react'
import { RxChevronDown } from 'react-icons/rx'
import { tss } from 'tss-react'
import { brightBlue, gray } from '~/common/styles/colors'
import { ApplicationOrientation } from '../../../../common/generated/apollo-operations'
import { useGlobalContext } from '../../../../common/hooks/useGlobalContext'
import { getTabColorPrimitives, getTabWithSelectionShape } from './primitives'
import { TabSvgProps, TabVariant } from './types'

/**
 * TabSvg is a component that represents a tab in the tab bar.
 */
export const TabSvg = forwardRef(function TabSvg(
  {
    children,
    className,
    selected,
    variant = 'primary',
    handleClick,
    showChevron,
    ...rest
  }: TabSvgProps,
  ref,
) {
  const { user } = useGlobalContext()
  const { classes, cx } = useStyles({
    variant,
    selected,
    orientation: user?.orientation ?? ApplicationOrientation.LEFT_TO_RIGHT,
  })
  return (
    <Box
      ref={ref}
      className={cx(classes.svgContainer, className)}
      onClick={handleClick}
      zIndex={selected ? 1 : 0}
      {...rest}
    >
      {getTabWithSelectionShape(selected)}
      <Box className={cx(classes.svgContent, className, 'svgContent')}>
        {children}
        {selected && showChevron && (
          <Box className={cx(classes.caret, className, 'caret')}>
            <RxChevronDown fontSize="16px" />
          </Box>
        )}
      </Box>
    </Box>
  )
})

export const useStyles = tss
  .withParams<{ variant: TabVariant; selected: boolean; orientation: ApplicationOrientation }>()
  .create(({ variant, selected, orientation }) => {
    const colorPrimitives = getTabColorPrimitives(variant, selected)

    let svgLeft = '11.5%'
    let svgRight = '0'
    let caretLeft: string | undefined = undefined
    let caretRight: string | undefined = '14px'
    if (orientation === ApplicationOrientation.RIGHT_TO_LEFT) {
      svgLeft = '0'
      svgRight = '11.5%'
      caretLeft = '14px'
      caretRight = undefined
    }
    return {
      svgContainer: {
        position: 'relative',
        width: '220px',
        height: '50px',
        cursor: 'pointer',
        top: selected ? '0px' : '2px',
        pointerEvents: 'auto',
        '&:hover .svgContent': {
          color: colorPrimitives.hover.color,
        },
        '&:hover .caret': {
          color: colorPrimitives.hover.color,
          transform: 'translateY(1px)',
        },
        '& .svgTabOutline': {
          width: '100%',
          height: '100%',
          '& .svgTabOutlinePath': {
            ...colorPrimitives.default,
            strokeWidth: 0.7,
          },
          '&:hover .svgTabOutlinePath': {
            strokeWidth: 0.7,
            ...colorPrimitives.hover,
          },
        },
        '& .svgTabOutlineBorder': {
          width: '100%',
          height: '100%',
          '& .svgTabOutlineBorderPath': {
            stroke: variant === 'primary' ? gray[2] : brightBlue,
            strokeWidth: variant === 'primary' ? 2 : 1.5,
          },
        },
      },
      caret: {
        display: 'inline-block',
        position: 'absolute',
        right: caretRight,
        left: caretLeft,
        top: '14px',
        color: gray[6],
        transition: 'color 0.3s ease, transform 0.3s ease',
      },
      svgContent: {
        position: 'absolute',
        top: '4px',
        left: svgLeft,
        right: svgRight,
        bottom: 0,
        display: 'flex',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        pointerEvents: 'none',
        color: colorPrimitives.default.color,
        '&:hover': {
          color: colorPrimitives.hover.color,
        },
      },
    }
  })
